import { globalConfirm } from '../components/ConfirmDialog';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AppShell from '../components/layout/AppShell';
import { PageHeader, DataTableToolbar, PaginationBar, ActiveBadge, EmptyState, LoadingSpinner } from '../components/layout/SharedComponents';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { AddressLookup, PostcodeLookup } from '../components/AddressLookup';
import InteractiveTable from '../components/InteractiveTable';
import api, { getApiErrorMessage } from '../services/api';
import ModuleReports from './Reports/ModuleReports';
import { CUSTOMERS_REPORTS } from './Reports/reportConfigs';
import { BarChart2, FileText as FileTextIcon, LayoutDashboard } from 'lucide-react';
import CustomersDashboard from './CustomersDashboard';
import { toast } from 'sonner';
import { usePermissions } from '../hooks/usePermissions';
import { Plus, Download, Trash2, Users } from 'lucide-react';

const CUSTOMER_TYPES = ['B2B', 'B2C', 'Government', 'Education', 'Non-Profit', 'Other'];
const COUNTRIES = ['United States','United Kingdom','Canada','Australia','Germany','France','UAE','India','Singapore','Japan','Other'];

const EMPTY = {
  customer_code: '', customer_name: '', customer_type: 'B2B',
  address: '', city: '', state_province: '', zip_postal: '', country: 'United States',
  telephone: '', fax: '', email: '', website: '',
  account_manager: '', tax_id: '', registration_number: '',
  credit_limit: 0, payment_terms: 'Net 30', currency: 'USD',
  billing_address: '', billing_city: '', billing_country: 'United States',
  active: true, contacts: [],
};

export default function CustomersPage() {
  const [records, setRecords] = useState([]);
  const { canWrite: _cw } = usePermissions();
  const canWriteData = _cw('customers');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [viewTab, setViewTab] = useState('list');
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [sortState, setSortState] = useState({ field: 'customer_code', dir: 'asc' });
  const handleSort = useCallback((field) => {
    setSortState(prev => prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' });
  }, []);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [isEdit, setIsEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', email: '', phone: '', is_main: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/customers', { params: { search, page, page_size: pageSize, sort_by: sortState.field, sort_dir: sortState.dir } });
      setRecords(res.data.data); setTotal(res.data.total);
    } catch (err) { if (err?.response?.status >= 500) toast.error('Failed to load customers'); }
    setLoading(false);
  }, [search, page, pageSize, sortState]);

  useEffect(() => { load(); }, [load]);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const openEdit = async code => {
    try { const res = await api.get(`/api/customers/${code}`); setForm({ ...EMPTY, ...res.data }); setIsEdit(true); setModalOpen(true); }
    catch (err) { if (err?.response?.status >= 500) toast.error('Failed to load'); }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (isEdit) await api.put(`/api/customers/${form.customer_code}`, form);
      else await api.post('/api/customers', form);
      toast.success(isEdit ? 'Updated' : 'Created'); setModalOpen(false); load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Save failed')); }
    setSaving(false);
  };

  const deleteRecord = async () => {
    if (!await globalConfirm('Delete this customer?')) return;
    try { await api.delete(`/api/customers/${form.customer_code}`); toast.success('Deleted'); setModalOpen(false); load(); }
    catch (err) { if (err?.response?.status >= 500) toast.error('Delete failed'); }
  };

  const addContact = () => {
    if (!newContact.name) return;
    setForm(p => ({ ...p, contacts: [...p.contacts, { ...newContact }] }));
    setNewContact({ name: '', email: '', phone: '', is_main: false });
  };


  const toggleFav = async (r) => {
    const { toggleFavorite } = await import('../utils/favorites');
    const result = await toggleFavorite('customers', r.customer_code);
    if (result !== null) {
      setRecords(prev => {
        const updated = prev.map(rec => (rec === r) ? { ...rec, is_favorite: result } : rec);
        const starred = updated.filter(rec => rec.is_favorite);
        const unstarred = updated.filter(rec => !rec.is_favorite);
        return [...starred, ...unstarred];
      });
    }
  };

  const custDefaultCols = useMemo(() => [
    { key: 'customer_code', label: 'Customer Code', width: 140, sortField: 'customer_code', visible: true },
    { key: 'customer_name', label: 'Customer Name', width: 200, sortField: 'customer_name', visible: true },
    { key: 'customer_type', label: 'Type', width: 100, sortField: 'customer_type', visible: true },
    { key: 'city', label: 'City', width: 120, sortField: 'city', visible: true },
    { key: 'country', label: 'Country', width: 130, sortField: 'country', visible: true },
    { key: 'payment_terms', label: 'Payment Terms', width: 130, sortField: 'payment_terms', visible: true },
    { key: 'active', label: 'Active', width: 80, sortField: 'active', visible: true },
    { key: 'address', label: 'Address', width: 220, visible: true, addressCol: true },
    { key: 'state_province', label: 'State / Province', width: 140, visible: true, addressCol: true },
    { key: 'zip_postal', label: 'Zip / Postal', width: 110, visible: true, addressCol: true },
    { key: 'email', label: 'Email', width: 200, visible: true, addressCol: true },
    { key: 'telephone', label: 'Telephone', width: 140, visible: true, addressCol: true },
  ], []);

  const renderCustCell = useCallback((col, r) => {
    switch (col.key) {
      case 'customer_code': return <span className="font-mono font-semibold text-zinc-900 text-sm">{r.customer_code}</span>;
      case 'customer_name': return <span className="font-medium text-sm">{r.customer_name}</span>;
      case 'customer_type': return <Badge variant="outline" className="text-xs">{r.customer_type}</Badge>;
      case 'city': return <span className="text-sm">{r.city}</span>;
      case 'country': return <span className="text-sm">{r.country}</span>;
      case 'payment_terms': return <span className="text-sm">{r.payment_terms}</span>;
      case 'active': return <ActiveBadge active={r.active} />;
      default: return <span className="text-sm">{r[col.key] != null ? String(r[col.key]) : '—'}</span>;
    }
  }, []);

  return (
    <AppShell>
      <PageHeader title="Customers" description={`Total records: ${total}`} helpGuide="data-files"
        actions={viewTab === 'list' ? <>
          <Button size="sm" variant="outline"><Download className="h-4 w-4 mr-1" />Export</Button>
          {canWriteData && <Button size="sm" onClick={() => { setForm(EMPTY); setIsEdit(false); setModalOpen(true); }} data-testid="customer-add-button">
            <Plus className="h-4 w-4 mr-1" />Add New
          </Button>}
        </> : null}
      />
      <div className="flex gap-4 border-b mb-4" data-testid="customers-view-tabs">
        <button onClick={() => setViewTab('analytics')} data-testid="customers-tab-analytics"
          className={`pb-2 text-sm font-medium transition-colors ${viewTab === 'analytics' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <LayoutDashboard className="h-4 w-4 inline mr-1.5" /> Analytics
        </button>
        <button onClick={() => setViewTab('list')} data-testid="customers-tab-list"
          className={`pb-2 text-sm font-medium transition-colors ${viewTab === 'list' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <FileTextIcon className="h-4 w-4 inline mr-1.5" /> Customers
        </button>
        <button onClick={() => setViewTab('reports')} data-testid="customers-tab-reports"
          className={`pb-2 text-sm font-medium transition-colors ${viewTab === 'reports' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <BarChart2 className="h-4 w-4 inline mr-1.5" /> Reports
        </button>
      </div>
      {viewTab === 'analytics' ? (
        <CustomersDashboard />
      ) : viewTab === 'reports' ? (
        <ModuleReports reports={CUSTOMERS_REPORTS} apiPrefix="/api/module-reports/data" module="Customers" />
      ) : (<>
      <DataTableToolbar searchValue={search} onSearch={v => { setSearch(v); setPage(1); }} onClear={() => { setSearch(''); setPage(1); }} searchPlaceholder="Search customers..." toolbarSlotId="customers-listing-toolbar" />
      <div className="rounded-xl border bg-card overflow-hidden">
        {loading ? <div className="py-12"><LoadingSpinner /></div> :
        records.length === 0 ? <EmptyState title="No customers" action={<Button size="sm" onClick={() => { setForm(EMPTY); setIsEdit(false); setModalOpen(true); }}>Add Customer</Button>} /> : (
          <InteractiveTable
            tableId="customers-listing"
            toolbarPortalId="customers-listing-toolbar"
            defaultCols={custDefaultCols}
            data={records}
            renderCell={renderCustCell}
            onRowClick={(row) => openEdit(row.customer_code)}
            sortState={sortState}
            onSort={handleSort}
            rowKey="customer_code"
          />
        )}
        <div className="px-4"><PaginationBar page={page} pageSize={pageSize} total={total} onPageChange={setPage} onPageSizeChange={v => { setPageSize(v); setPage(1); }} /></div>
      </div>
      </>)}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              {isEdit ? `Edit Customer — ${form.customer_name}` : 'Add Customer'}
              <div className="flex items-center gap-2 mr-6"><span className="text-sm font-normal text-muted-foreground">Active</span><Switch checked={form.active} onCheckedChange={v => f('active', v)} /></div>
            </DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="details">
            <TabsList><TabsTrigger value="details">Customer Details</TabsTrigger><TabsTrigger value="billing">Billing & Finance</TabsTrigger><TabsTrigger value="contacts">Contacts ({(form.contacts||[]).length})</TabsTrigger></TabsList>

            <TabsContent value="details" className="pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Customer Code *</Label><Input value={form.customer_code} onChange={e => f('customer_code', e.target.value)} disabled={isEdit} /></div>
                <div className="space-y-1.5"><Label>Customer Name *</Label><Input value={form.customer_name} onChange={e => f('customer_name', e.target.value)} /></div>
                <div className="space-y-1.5"><Label>Customer Type</Label>
                  <select value={form.customer_type} onChange={e => f('customer_type', e.target.value)} className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm">
                    {CUSTOMER_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5"><Label>Website</Label><Input value={form.website} onChange={e => f('website', e.target.value)} placeholder="www.example.com" /></div>
                <div className="col-span-2 space-y-2 border rounded-xl p-3 bg-muted/10">
                  <div className="flex items-center justify-between">
                    <Label className="font-semibold text-xs">Address Lookup</Label>
                    <span className="text-[10px] text-muted-foreground">Auto-fills fields below · US &amp; UK</span>
                  </div>
                  <AddressLookup placeholder="Type address to auto-fill…"
                    onSelect={({ address, city, state_province, zip_postal, country }) => {
                      if (address)       f('address', address);
                      if (city)          f('city', city);
                      if (state_province)f('state_province', state_province);
                      if (zip_postal)    f('zip_postal', zip_postal);
                      if (country)       f('country', country);
                    }} />
                </div>
                <div className="col-span-2 space-y-1.5"><Label>Address</Label><Input value={form.address} onChange={e => f('address', e.target.value)} /></div>
                <div className="space-y-1.5"><Label>City</Label><Input value={form.city} onChange={e => f('city', e.target.value)} /></div>
                <div className="space-y-1.5"><Label>State / Province</Label><Input value={form.state_province} onChange={e => f('state_province', e.target.value)} /></div>
                <div className="space-y-1.5">
                  <Label>Zip / Postal Code</Label>
                  <Input value={form.zip_postal} onChange={e => f('zip_postal', e.target.value)} />
                  {form.country === 'United Kingdom' && (
                    <div className="mt-1.5"><PostcodeLookup onFound={({ zip_postal, city, state_province }) => {
                      f('zip_postal', zip_postal);
                      if (city)           f('city', city);
                      if (state_province) f('state_province', state_province);
                    }} /></div>
                  )}
                </div>
                <div className="space-y-1.5"><Label>Country</Label>
                  <select value={form.country} onChange={e => f('country', e.target.value)} className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm">
                    {COUNTRIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5"><Label>Telephone</Label><Input value={form.telephone} onChange={e => f('telephone', e.target.value)} /></div>
                <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={e => f('email', e.target.value)} /></div>
                <div className="space-y-1.5"><Label>Tax ID</Label><Input value={form.tax_id} onChange={e => f('tax_id', e.target.value)} /></div>
                <div className="space-y-1.5"><Label>Registration Number</Label><Input value={form.registration_number} onChange={e => f('registration_number', e.target.value)} /></div>
                <div className="space-y-1.5"><Label>Account Manager</Label><Input value={form.account_manager} onChange={e => f('account_manager', e.target.value)} /></div>
              </div>
            </TabsContent>

            <TabsContent value="billing" className="pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Payment Terms</Label><Input value={form.payment_terms} onChange={e => f('payment_terms', e.target.value)} placeholder="Net 30" /></div>
                <div className="space-y-1.5"><Label>Currency</Label><Input value={form.currency} onChange={e => f('currency', e.target.value)} placeholder="USD" /></div>
                <div className="space-y-1.5"><Label>Credit Limit</Label><Input type="number" value={form.credit_limit} onChange={e => f('credit_limit', parseFloat(e.target.value)||0)} /></div>
                <div className="col-span-2 space-y-1.5"><Label>Billing Address (if different from main)</Label><Input value={form.billing_address} onChange={e => f('billing_address', e.target.value)} /></div>
                <div className="space-y-1.5"><Label>Billing City</Label><Input value={form.billing_city} onChange={e => f('billing_city', e.target.value)} /></div>
                <div className="space-y-1.5"><Label>Billing Country</Label>
                  <select value={form.billing_country} onChange={e => f('billing_country', e.target.value)} className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm">
                    {COUNTRIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="contacts" className="pt-4">
              <div className="flex items-center gap-2 mb-4">
                <Input placeholder="Name" value={newContact.name} onChange={e => setNewContact(p=>({...p,name:e.target.value}))} className="h-8" />
                <Input placeholder="Email" value={newContact.email} onChange={e => setNewContact(p=>({...p,email:e.target.value}))} className="h-8" />
                <Input placeholder="Phone" value={newContact.phone} onChange={e => setNewContact(p=>({...p,phone:e.target.value}))} className="h-8 w-28" />
                <div className="flex items-center gap-1"><Switch checked={newContact.is_main} onCheckedChange={v=>setNewContact(p=>({...p,is_main:v}))} /><Label className="text-xs">Main</Label></div>
                <Button size="sm" onClick={addContact}><Plus className="h-4 w-4" /></Button>
              </div>
              {(form.contacts||[]).length === 0 ? <EmptyState title="No contacts" /> : (
                <Table><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead></TableHead></TableRow></TableHeader>
                  <TableBody>{form.contacts.map((c,i)=>(
                    <TableRow key={i}><TableCell className="text-sm">{c.is_main && <span className="inline-block w-2 h-2 rounded-full bg-primary mr-1"></span>}{c.name}</TableCell>
                      <TableCell className="text-sm">{c.email}</TableCell><TableCell className="text-sm">{c.phone}</TableCell>
                      <TableCell><Button size="sm" variant="ghost" onClick={()=>setForm(p=>({...p,contacts:p.contacts.filter((_,j)=>j!==i)}))}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button></TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              )}
            </TabsContent>
          </Tabs>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            {isEdit && <Button variant="destructive" onClick={deleteRecord}>Delete</Button>}
            {canWriteData && <Button onClick={save} disabled={saving || !form.customer_code || !form.customer_name}>{saving ? 'Saving...' : 'Save'}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
